import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Layout, Spin, Alert, Card, Button, Form, Input } from 'antd'
import { LoadingOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { useCustomerAuth } from '../contexts/CustomerAuthContext'
import { customerApi } from '../services/customerApi'

const { Content } = Layout

export const AccessPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { login, portalConfig } = useCustomerAuth()

  const [pageBusy, setPageBusy] = useState(true)
  const [submittingPassword, setSubmittingPassword] = useState(false)
  const [awaitingPassword, setAwaitingPassword] = useState(false)
  const [businessHint, setBusinessHint] = useState<string | undefined>(undefined)

  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form] = Form.useForm()
  const lastInitialTokenRef = useRef<string | null>(null)

  const handleSuccessFlow = useCallback(
    async (
      response: Awaited<ReturnType<typeof customerApi.verifyPortalLink>>
    ): Promise<void> => {
      const tokenToUse = response.customerToken || response.token
      if (!response.success || !tokenToUse) {
        setError('Invalid or expired access link')
        return
      }

      await login(tokenToUse)
      setSuccess(true)

      setTimeout(() => {
        // Magic link proves identity — never send users to mandatory password-setup; optional password lives in Profile/login.
        const redirectPath = response.redirectUrl || response.redirectTo
        const dest =
          redirectPath && redirectPath !== '/setup-password'
            ? redirectPath
            : response.portalConfig?.defaultLandingPage ||
              portalConfig?.defaultLandingPage ||
              '/dashboard'
        navigate(dest, { replace: true })
      }, 1500)
    },
    [login, navigate, portalConfig?.defaultLandingPage]
  )

  const runVerification = useCallback(
    async (magicToken: string, portalPassword?: string) => {
      const isPasswordStep = portalPassword !== undefined
      if (!isPasswordStep) {
        setPageBusy(true)
        setAwaitingPassword(false)
        setBusinessHint(undefined)
      } else {
        setSubmittingPassword(true)
      }
      setError(null)

      try {
        const response = await customerApi.verifyPortalLink({
          token: magicToken,
          password: portalPassword,
        })
        await handleSuccessFlow(response)
      } catch (err: unknown) {
        const axiosErr = err as {
          response?: { status?: number; data?: { code?: string; message?: string; businessName?: string } }
          message?: string
        }
        const status = axiosErr.response?.status
        const code = axiosErr.response?.data?.code

        if (status === 401 && code === 'PASSWORD_REQUIRED') {
          setAwaitingPassword(true)
          setBusinessHint(
            typeof axiosErr.response?.data?.businessName === 'string'
              ? axiosErr.response.data.businessName
              : undefined
          )
          return
        }

        if (status === 401 && code === 'INVALID_PASSWORD') {
          setError(
            axiosErr.response?.data?.message ||
              'That code is incorrect. Check your email or text message and try again.'
          )
          return
        }

        if (status === 401) {
          setError(
            axiosErr.response?.data?.message ||
              'This access link has expired, is invalid, or has already been used.'
          )
          return
        }

        if (status === 404) {
          setError('Invalid access link')
          return
        }

        setError('Unable to access your portal. Please try again or contact support.')
      } finally {
        setPageBusy(false)
        setSubmittingPassword(false)
      }
    },
    [handleSuccessFlow]
  )

  useEffect(() => {
    if (!token) {
      setError('Invalid access link - no token provided')
      setPageBusy(false)
      return
    }
    if (lastInitialTokenRef.current === token) {
      return
    }
    lastInitialTokenRef.current = token
    void runVerification(token)
  }, [token, runVerification])

  const onPasswordFinish = (values: { password: string }) => {
    if (!token) return
    void runVerification(token, values.password)
  }

  const handleRetry = () => {
    if (!token) return
    form.resetFields()
    setError(null)
    setAwaitingPassword(false)
    void runVerification(token)
  }

  const showInitialSpinner = pageBusy && !awaitingPassword
  const showPasswordGate = awaitingPassword && !success

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <Content
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '24px',
        }}
      >
        <Card
          style={{
            width: '100%',
            maxWidth: '400px',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
          bordered={false}
        >
          {showInitialSpinner && (
            <>
              <Spin
                indicator={
                  <LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />
                }
                style={{ display: 'block', marginBottom: '24px' }}
              />
              <h2 style={{ marginBottom: '16px' }}>Accessing Your Portal</h2>
              <p style={{ color: '#666', marginBottom: 0 }}>
                Please wait while we verify your access link...
              </p>
            </>
          )}

          {showPasswordGate && !pageBusy && (
            <>
              <h2 style={{ marginBottom: '12px' }}>Portal access code required</h2>
              <p style={{ color: '#666', marginBottom: '16px', textAlign: 'left' }}>
                {businessHint
                  ? `${businessHint} secured this link. Use the portal access code from your email or text message, then sign in below.`
                  : 'Use the portal access code from your email or text message, then sign in below.'}
              </p>
              {error ? (
                <Alert
                  type="error"
                  message={error}
                  showIcon
                  style={{ marginBottom: '16px', textAlign: 'left' }}
                />
              ) : null}
              <Form form={form} layout="vertical" onFinish={onPasswordFinish}>
                <Form.Item
                  name="password"
                  label="Portal access code"
                  rules={[{ required: true, message: 'Enter your portal access code' }]}
                  style={{ textAlign: 'left' }}
                >
                  <Input.Password autoComplete="one-time-code" placeholder="Access code" />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={submittingPassword} block>
                  Continue
                </Button>
              </Form>
            </>
          )}

          {success && (
            <>
              <CheckCircleOutlined
                style={{ fontSize: 48, color: '#52c41a', marginBottom: '24px' }}
              />
              <h2 style={{ marginBottom: '16px', color: '#52c41a' }}>Access Granted!</h2>
              <p style={{ color: '#666', marginBottom: 0 }}>
                Welcome to your ServiceFlow Pro customer portal. Redirecting you now...
              </p>
            </>
          )}

          {error && !showPasswordGate && (
            <>
              <ExclamationCircleOutlined
                style={{ fontSize: 48, color: '#ff4d4f', marginBottom: '24px' }}
              />
              <h2 style={{ marginBottom: '16px', color: '#ff4d4f' }}>Access Error</h2>
              <Alert
                message={error}
                type="error"
                showIcon={false}
                style={{ marginBottom: '24px', textAlign: 'left' }}
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <Button onClick={handleRetry} type="primary">
                  Try Again
                </Button>
                <Button onClick={() => navigate('/')} type="default">
                  Go Home
                </Button>
              </div>
            </>
          )}

          <div
            style={{
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid #f0f0f0',
              fontSize: '12px',
              color: '#999',
            }}
          >
            <p style={{ margin: 0 }}>ServiceFlow Pro Customer Portal</p>
            <p style={{ margin: 0 }}>Secure customer access</p>
          </div>
        </Card>
      </Content>
    </Layout>
  )
}
